import React, { useEffect, useState } from 'react'

// Centralized lodash imports - much cleaner!
import {
  remove,
  map,
  slice,
  orderBy,
  isEmpty,
  includes,
  get,
} from '@/utils/lodash'

import { connect } from 'react-redux'

import {
  Grid,
  Avatar,
  AppBar,
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material'

import {
  setSidebarToggleMobile,
  setNotificationsDrawerToggle,
} from '../../reducers/ThemeOptions'
import { headerBarSx } from '@/layout-blueprints/chromeSx'
import {
  headerPaneSx,
  headerPaneEndSx,
  notificationActionLinkSx,
  notificationActionTextSx,
  notificationAvatarIconSx,
  notificationDrawerBodySx,
  notificationDrawerSectionSpacerSx,
  notificationDrawerTitleSx,
  notificationListItemCenterSx,
  notificationListItemFlexSpacerSx,
  headerTrialUpgradeBtnSx,
  notificationDrawerPaperSx,
  drawerBackdropSx,
  drawerModalRootSx,
} from '@/layout-blueprints/chromeComponentSx'
import HamburgerButton from '@/layout-components/HamburgerButton'
import CloseIcon from '@mui/icons-material/Close'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import HeaderDots from '@/layout-components/HeaderDots'
import HeaderUserbox from '@/layout-components/HeaderUserbox'
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined'
import IconButton from '@mui/material/IconButton'
import withSession from '@/housefolios-components/Session/withSession'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  GET_NOTIFICATIONS,
  GET_UNREAD_NOTIFICATIONS_COUNT,
  NOTIFICATION_REFETCH_QUERIES,
  UNREAD_NOTIFICATIONS_COUNT_POLL_MS,
} from '@/housefolios-components/Notifications/queries'
import { useMutation, useQuery } from '@apollo/client/react'
import { MARK_OLD } from '@/housefolios-components/Notifications/mutations'
import ManageButton from '@/housefolios-components/Notifications/ManageButton'
import Realeflow from '@/housefolios-components/Realeflow'

import { GET_AFFILIATES } from '@/housefolios-components/CreateAccount/CreateAccountCard/queries'
import { UPDATE_ORGANIZATION } from '@/housefolios-components/OrganizationSettings/mutations'
import { GET_ME } from '@/housefolios-components/Session/queries'
import { GET_WORKFLOWS } from '@/housefolios-components/Properties/queries'
import { GET_CARDS } from '@/housefolios-components/Properties/queries'
import { useNavigate } from 'react-router'
import useAffiliate from '@/hooks/useAffiliate'

const Header = (props) => {
  const {
    headerShadow,
    headerBgTransparent,
    sidebarToggleMobile,
    setSidebarToggleMobile,
    sidebarToggle,
  } = props

  const me = props?.session?.me

  const DEALSAUCE_2_0_URL = 'https://app.dealsauce.io'
  const PEP_BETA_URL = 'https://app.propertyedge.pro'
  const BETA_HOT_PLAN_ROLLOUT_DATE = new Date('2026-06-30T00:00:00')
  const BETA_ALL_MEMBERS_ROLLOUT_DATE = new Date(
    '2026-07-13T00:00:00',
  )
  const DEALSAUCE_MEMBER_VALUES = ['pacemorby', 'astroflip']

  const [{ affiliate: brandAffiliate, urlHelmetData, urlDomain }] =
    useAffiliate({
      memberValue: me?.activeOrg?.member,
    })

  const isDealSauceHost = React.useMemo(() => {
    const domain =
      urlDomain ||
      (typeof window !== 'undefined' ? window.location.host : '')
    return String(domain).includes('dealsauce')
  }, [urlDomain])

  const isPepHost = React.useMemo(() => {
    const domain =
      urlDomain ||
      (typeof window !== 'undefined' ? window.location.host : '')
    return String(domain).includes('propertyedge')
  }, [urlDomain])

  const isSuperAdmin =
    me?.roles?.some((r) => r?.role?.[0] === 'superAdmin') ?? false

  const BETA_AFFILIATE_MEMBERS = ['pep', 'pacemorby', 'astroflip']

  const isBetaAffiliateAdmin = React.useMemo(() => {
    const adminMembers = Array.isArray(me?.affiliateAdmin)
      ? me.affiliateAdmin
      : []
    if (
      adminMembers.some((m) => BETA_AFFILIATE_MEMBERS.includes(m))
    ) {
      return true
    }
    const affiliateMember =
      me?.activeOrg?.isAffiliate?.memberValue || me?.activeOrg?.member
    return (
      Boolean(me?.activeOrg?.isAffiliate) &&
      BETA_AFFILIATE_MEMBERS.includes(affiliateMember)
    )
  }, [me])

  const isInternalTester =
    isSuperAdmin ||
    Boolean(me?.housefoliosAdmin) ||
    isBetaAffiliateAdmin

  const isHotPlan = React.useMemo(() => {
    const subs = Array.isArray(me?.activeOrg?.subscriptions)
      ? me.activeOrg.subscriptions
      : []
    return subs.some((s) => {
      if (!s || s.disabled) return false
      const rfId =
        s?.realeflowPlanId ?? s?.plan?.realeflowPlanId ?? null
      const planType = String(
        s?.planType || s?.planNickname || '',
      ).toLowerCase()
      const status = String(s?.status || '').toLowerCase()
      const activeish =
        status === '' ||
        ['active', 'trialing', 'past_due'].includes(status)
      const isHotById = ['1250', '1252', 1250, 1252].includes(rfId)
      const isHotByType = planType.includes('hot')
      return activeish && (isHotById || isHotByType)
    })
  }, [me])

  const isDealSauceMember = DEALSAUCE_MEMBER_VALUES.includes(
    me?.activeOrg?.member,
  )

  const isPastHotPlanRollout =
    new Date() >= BETA_HOT_PLAN_ROLLOUT_DATE
  const isPastAllMembersRollout =
    new Date() >= BETA_ALL_MEMBERS_ROLLOUT_DATE
  const showBetaToggle =
    isDealSauceHost &&
    (isInternalTester ||
      (isPastHotPlanRollout && isHotPlan) ||
      (isPastAllMembersRollout && isDealSauceMember))

  const isPepMember = me?.activeOrg?.member === 'pep'
  const showPepBetaToggle =
    isPepHost && (isInternalTester || isPepMember)

  const dealSauceLogoUrl = React.useMemo(() => {
    if (!isEmpty(brandAffiliate)) {
      return (
        get(
          brandAffiliate,
          'sidebar.sidebarMemberSmallImage.0.url',
        ) ||
        get(brandAffiliate, 'sidebar.sidebarMemberFullImage.0.url') ||
        urlHelmetData?.icon ||
        ''
      )
    }
    return urlHelmetData?.icon || ''
  }, [brandAffiliate, urlHelmetData])

  const hostname = import.meta.env.VITE_HOSTNAME

  const [currentMember, setCurrentMember] = useState(
    props?.session?.me?.activeOrg?.member || '',
  )

  const { loading: affiliateLoading, data: affiliateData } = useQuery(
    GET_AFFILIATES,
    { skip: !me },
  )

  const { affiliates } = affiliateData || {}

  const [updateOrganization] = useMutation(UPDATE_ORGANIZATION)

  const changeOrgMember = (newMember) => {
    updateOrganization({
      variables: {
        id: props.session.me.activeOrg._id,
        organizationInput: {
          member: newMember || '',
        },
      },
      refetchQueries: [
        { query: GET_ME },
        { query: GET_WORKFLOWS },
        { query: GET_CARDS },
      ],
    })
  }

  const toggleSidebarMobile = () => {
    setSidebarToggleMobile(!sidebarToggleMobile)
  }

  let navigate = useNavigate()

  const goBack = () => {
    navigate(-1)
  }

  const today = new Date()

  const [markOld] = useMutation(MARK_OLD, {
    refetchQueries: NOTIFICATION_REFETCH_QUERIES,
  })

  const { notificationsDrawerToggle, setNotificationsDrawerToggle } =
    props

  const toggleNotificationsDrawer = () => {
    setNotificationsDrawerToggle(!notificationsDrawerToggle)
  }

  const { data: unreadCountData } = useQuery(
    GET_UNREAD_NOTIFICATIONS_COUNT,
    {
      pollInterval: UNREAD_NOTIFICATIONS_COUNT_POLL_MS,
      skip: !me,
    },
  )

  const {
    loading: notificationsLoading,
    error: notificationsError,
    data,
  } = useQuery(GET_NOTIFICATIONS, {
    skip: !me || !notificationsDrawerToggle,
    fetchPolicy: 'cache-and-network',
  })

  if (!me) return null

  const unreadCount = unreadCountData?.unreadNotificationsCount || 0

  let notifications = [...(data?.notifications || [])]

  let oldNotifications = remove(notifications, ['old', true])

  remove(notifications, ['old', true])

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        color="inherit"
        data-hf="chrome-header"
        sx={headerBarSx({
          collapsed: sidebarToggle,
          headerShadow,
          headerBgTransparent,
        })}
      >
        <Box sx={headerPaneSx}>
          <HamburgerButton
            active={sidebarToggleMobile}
            showBelowMl
            onClick={toggleSidebarMobile}
          />
          {!props.hideBackButton && (
            <Tooltip title="Previous Page">
              <IconButton onClick={goBack} size="large">
                <KeyboardBackspaceOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box sx={headerPaneEndSx}>
          {me?.subscriptionPlan === 'trial' &&
            me?.activeOrg?.member !== 'pep' && (
              <Button
                variant="contained"
                margin="dense"
                color="warning"
                sx={headerTrialUpgradeBtnSx}
                onClick={props.toggleSubscribe}
              >
                <Typography>Upgrade</Typography>
              </Button>
            )}
          {showBetaToggle && (
            <Tooltip title="Switch to the new Deal Sauce experience">
              <Button
                variant="contained"
                disableElevation
                onClick={() => {
                  window.location.href = DEALSAUCE_2_0_URL
                }}
                sx={{
                  marginRight: 2,
                  borderRadius: '20px',
                  px: 1.25,
                  py: 0.5,
                  minHeight: 36,
                  flexShrink: 0,
                  background:
                    'linear-gradient(180deg, #6B1800 0%, #571300 100%)',
                  color: '#F5E6D3',
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '0.02em',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 1px 4px rgba(87, 19, 0, 0.3)',
                  border: '2px solid #4a0f00',
                  '&:hover': {
                    background:
                      'linear-gradient(180deg, #7A1C00 0%, #631600 100%)',
                    boxShadow: '0 2px 8px rgba(87, 19, 0, 0.4)',
                  },
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                }}
              >
                {dealSauceLogoUrl && (
                  <Box
                    component="img"
                    src={dealSauceLogoUrl}
                    alt="Deal Sauce"
                    sx={{
                      height: 24,
                      width: 'auto',
                      maxWidth: 72,
                      objectFit: 'contain',
                      flexShrink: 0,
                    }}
                  />
                )}
                Try Deal Sauce 2.0!
              </Button>
            </Tooltip>
          )}
          {showPepBetaToggle && (
            <Tooltip title="Switch to the new Property Edge experience">
              <Button
                variant="contained"
                disableElevation
                onClick={() => {
                  window.location.href = PEP_BETA_URL
                }}
                sx={{
                  marginRight: 2,
                  borderRadius: '20px',
                  px: 1.25,
                  py: 0.5,
                  minHeight: 36,
                  flexShrink: 0,
                  background:
                    'linear-gradient(180deg, #6B1800 0%, #571300 100%)',
                  color: '#F5E6D3',
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '0.02em',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 1px 4px rgba(87, 19, 0, 0.3)',
                  border: '2px solid #4a0f00',
                  '&:hover': {
                    background:
                      'linear-gradient(180deg, #7A1C00 0%, #631600 100%)',
                    boxShadow: '0 2px 8px rgba(87, 19, 0, 0.4)',
                  },
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                }}
              >
                {dealSauceLogoUrl && (
                  <Box
                    component="img"
                    src={dealSauceLogoUrl}
                    alt="Property Edge"
                    sx={{
                      height: 24,
                      width: 'auto',
                      maxWidth: 72,
                      objectFit: 'contain',
                      flexShrink: 0,
                    }}
                  />
                )}
                Try PEP 2.0!
              </Button>
            </Tooltip>
          )}
          {(hostname === 'localhost' ||
            hostname === 'dev-admin.housefolios.com' ||
            me?.housefoliosAdmin) &&
            !affiliateLoading && (
              <Select
                variant="outlined"
                margin="dense"
                displayEmpty
                value={currentMember}
                onChange={(event) => {
                  setCurrentMember(event.target.value)
                  changeOrgMember(event.target.value)
                }}
                sx={{
                  mr: '0.625rem',
                  minWidth: 160,
                  maxWidth: 200,
                  flexShrink: 1,
                  borderRadius: '0.65rem',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: '0.65rem',
                  },
                }}
              >
                <MenuItem key={'Not an Affiliate Member'} value={''}>
                  Not an Affiliate Member
                </MenuItem>
                {map(affiliates, (affiliate) => (
                  <MenuItem
                    key={affiliate?.companyName}
                    value={affiliate?.memberValue}
                  >
                    {affiliate?.companyName}
                  </MenuItem>
                ))}
              </Select>
            )}
          {props?.session?.me?.activeOrg?.member !== 'RWN' && (
            <HeaderDots {...props} />
          )}
          {me?.activeOrg?.isRealeflow && <Realeflow />}
          <IconButton
            onClick={toggleNotificationsDrawer}
            // onClick={isEmpty(notifications) ? () => props.history.push('/notifications') : handleOpenNotifications}
            // onClick={isEmpty(notifications) ? () => props.history.push('/notifications') : handleOpenMenu}
            size="large"
            sx={{ mr: 2 }}
          >
            <Badge
              badgeContent={unreadCount}
              color="primary"
            >
              <FontAwesomeIcon icon={['fal', 'bell']} sx={{ fontSize: "1.15rem !important" }} />
            </Badge>
          </IconButton>
          <HeaderUserbox {...props} />
        </Box>

        {/* <div>
            <Tooltip arrow title="Close drawer" placement="left">
              <Button
                size="small"
                onClick={toggleHeaderDrawer}
                id="CloseDrawerTooltip">
                <div)}>
                  <span>
                    <span />
                  </span>
                </div>
              </Button>
            </Tooltip>
            <div>
              <PerfectScrollbar>
                <Typography>Test</Typography>
              </PerfectScrollbar>
            </div>
          </div> */}

        {/* <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
            getContentAnchorEl={null}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center'
            }}
          >
            {map(slice(orderBy(notifications, 'actionDateTime', 'desc'), 0, 3), notification => {
              const time = new Date(notification.actionDateTime)
              const diff = today.getTime() - time.getTime()
              let timeLeftText = 'now'
              let timeLeft = diff / 1000
              if (timeLeft > 1) {
                timeLeftText = `${Math.floor(timeLeft)} seconds`
                timeLeft /= 60
                if (timeLeft > 1) {
                  timeLeftText = `${Math.floor(timeLeft)} minutes`
                  timeLeft /= 60
                  if (timeLeft > 1) {
                    timeLeftText = `${Math.floor(timeLeft)} hours`
                    timeLeft /= 24
                    if (timeLeft > 1) {
                      timeLeftText = `${Math.floor(timeLeft)} days`
                    }
                  }
                }
              }
              return (
                <MenuItem onClick={() => { props.history.push('/notifications'); handleCloseMenu() }}>
                  <ListItemText
                    primary={notification.action}
                    secondary={timeLeftText}
                  />
                  {!notification.system && (
                    <ListItemSecondaryAction>
                      <IconButton onClick={(event) =>  { markOld({ variables: { id: notification._id }}); event.stopPropagation(); }} edge="end">
                        <Check />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </MenuItem>
            )})}
            {isEmpty(notifications) && (
              <ListItem>
                <div>
                  <div>
                    <div style={{ backgroundColor: '#cfe1fa', color: '#008aff' }}>
                      <FontAwesomeIcon
                        icon={['fas', 'bell']}
                      />
                    </div>
                  </div>
                  <p>No New Notifications</p>
                </div>
              </ListItem>
            )}
            <MenuItem onClick={() => { props.history.push('/notifications'); handleCloseMenu() }}>See All Notifications</MenuItem>
          </Menu> */}
      </AppBar>

      <Drawer
        anchor="right"
        open={notificationsDrawerToggle}
        onClose={toggleNotificationsDrawer}
        slotProps={{
          paper: { sx: notificationDrawerPaperSx() },
          backdrop: { sx: drawerBackdropSx },
          root: { sx: drawerModalRootSx },
        }}
      >
        <Tooltip arrow title="Close drawer" placement="left">
          <IconButton size="small" onClick={toggleNotificationsDrawer} id="CloseNotificationDrawerTooltip" aria-label="Close notifications" sx={{ backgroundColor: '#fff', color: '#368cff', p: 0, overflow: 'visible', '&:hover': { backgroundColor: '#fff' }, '& svg': { color: '#368cff', fontSize: 20 }, }} data-hf="close-notification-drawer-btn">
            <CloseIcon />
          </IconButton>
        </Tooltip>
        <div>
          <Box sx={{
            position: "relative",
            height: "100%",
            maxHeight: "100%",
            overflowX: "hidden",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
          }}>
            <Box sx={{ p: '1.5rem' }}>
              <Grid
                container
                direction="row"
                spacing={1}
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Grid>
                  <Typography variant="h6" sx={notificationDrawerTitleSx}>
                    Notifications
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            <Divider />
            <Grid container direction="column" sx={notificationDrawerBodySx}>
              <Grid>
                <Grid container direction="column">
                  <Grid sx={notificationDrawerSectionSpacerSx}>
                    <Grid
                      container
                      direction="row"
                      sx={{
                        justifyContent: 'space-between',
                      }}
                    >
                      <Grid>
                        <Typography variant="subtitle1">
                          New
                        </Typography>
                      </Grid>
                      <Grid>
                        <Button
                          variant="text"
                          onClick={() => {
                            navigate('/notifications')
                          }}
                        >
                          See All
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid>
                    {notificationsLoading && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    )}
                    {notificationsError && (
                      <ListItem key="notifications-error" sx={notificationListItemCenterSx}>
                        <ListItemText>
                          {`Error! ${notificationsError.message}`}
                        </ListItemText>
                      </ListItem>
                    )}
                    <List>
                      {map(
                        slice(
                          orderBy(
                            notifications,
                            'actionDateTime',
                            'desc',
                          ),
                        ),
                        (notification, index) => {
                          const time = new Date(
                            notification.actionDateTime,
                          )
                          const diff =
                            today.getTime() - time.getTime()
                          let timeLeftText = 'now'
                          let timeLeft = diff / 1000
                          if (timeLeft > 1) {
                            timeLeftText = `${Math.floor(
                              timeLeft,
                            )} seconds`
                            timeLeft /= 60
                            if (timeLeft > 1) {
                              timeLeftText = `${Math.floor(
                                timeLeft,
                              )} minutes`
                              timeLeft /= 60
                              if (timeLeft > 1) {
                                timeLeftText = `${Math.floor(
                                  timeLeft,
                                )} hours`
                                timeLeft /= 24
                                if (timeLeft > 1) {
                                  timeLeftText = `${Math.floor(
                                    timeLeft,
                                  )} days`
                                }
                              }
                            }
                          }
                          return (
                            <>
                              <ListItem
                                key={`new ${index}`}
                                onClick={(event) => {
                                  navigate('/notifications')
                                  markOld({
                                    variables: {
                                      id: notification._id,
                                    },
                                  })
                                  event.stopPropagation()
                                }}
                              >
                                {/*
                                 */}
                                <ListItemText
                                  disableTypography={true}
                                  primary={
                                    <Grid container>
                                      <Grid size={12}>
                                        <Typography
                                          variant="subtitle1"
                                          sx={notificationActionTextSx}
                                        >
                                          {notification.action}{' '}
                                          {notification.actionLink ? (
                                            <Box
                                              component="a"
                                              href={
                                                notification.actionLink
                                              }
                                              target={
                                                notification.actionLinkTarget ||
                                                '_blank'
                                              }
                                              rel="noreferrer"
                                              sx={notificationActionLinkSx}
                                            >
                                              here
                                            </Box>
                                          ) : null}
                                        </Typography>
                                      </Grid>
                                      <Grid size={12}>
                                        <Typography
                                          variant="subtitle2"
                                          color="primary"
                                          sx={notificationActionTextSx}
                                        >
                                          {timeLeftText}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  }
                                  secondary={
                                    <Grid container>
                                      <Typography sx={notificationListItemFlexSpacerSx} />
                                      <ManageButton
                                        manageNotificationsButton={
                                          true
                                        }
                                        section={notification}
                                      />
                                    </Grid>
                                  }
                                />
                                <ListItemSecondaryAction>
                                  <IconButton
                                    onClick={(event) => {
                                      markOld({
                                        variables: {
                                          id: notification._id,
                                        },
                                      })
                                      event.stopPropagation()
                                    }}
                                    edge="end"
                                    size="large"
                                  >
                                    <FiberManualRecordIcon color="primary" />
                                  </IconButton>
                                </ListItemSecondaryAction>
                              </ListItem>
                            </>
                          )
                        },
                      )}
                      {isEmpty(notifications) && !notificationsLoading && (
                        <ListItem key="no new" sx={notificationListItemCenterSx}>
                          <ListItemText>
                            No New Notifications
                          </ListItemText>
                        </ListItem>
                      )}
                      {/* <MenuItem onClick={() => { props.history.push('/notifications'); handleCloseMenu() }}>See All Notifications</MenuItem> */}
                    </List>
                  </Grid>
                </Grid>
              </Grid>
              <Grid>
                <Grid container direction="column">
                  <Grid>
                    <Typography variant="subtitle1">Older</Typography>
                  </Grid>
                  <Grid>
                    <List>
                      {map(
                        slice(
                          orderBy(
                            oldNotifications,
                            'actionDateTime',
                            'desc',
                          ),
                        ),
                        (notification, index) => {
                          const time = new Date(
                            notification.actionDateTime,
                          )
                          const diff =
                            today.getTime() - time.getTime()
                          let timeLeftText = 'now'
                          let timeLeft = diff / 1000
                          if (timeLeft > 1) {
                            timeLeftText = `${Math.floor(
                              timeLeft,
                            )} seconds`
                            timeLeft /= 60
                            if (timeLeft > 1) {
                              timeLeftText = `${Math.floor(
                                timeLeft,
                              )} minutes`
                              timeLeft /= 60
                              if (timeLeft > 1) {
                                timeLeftText = `${Math.floor(
                                  timeLeft,
                                )} hours`
                                timeLeft /= 24
                                if (timeLeft > 1) {
                                  timeLeftText = `${Math.floor(
                                    timeLeft,
                                  )} days`
                                }
                              }
                            }
                          }
                          return (
                            <ListItem
                              key={`old ${index}`}
                              onClick={() => {
                                navigate('/notifications')
                              }}
                            >
                              <ListItemAvatar>
                                <Avatar>
                                  <FontAwesomeIcon
                                    icon={['fas', 'bell']}

                                    style={notificationAvatarIconSx}
                                  />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                disableTypography={true}
                                primary={
                                  <Grid container>
                                    <Grid size={12}>
                                      <Typography
                                        variant="subtitle1"
                                        sx={notificationActionTextSx}
                                      >
                                        {notification.action}
                                        {notification.actionLink ? (
                                          <Box
                                            component="a"
                                            href={
                                              notification.actionLink
                                            }
                                            target={
                                              notification.actionLinkTarget ||
                                              '_blank'
                                            }
                                            rel="noreferrer"
                                            sx={notificationActionLinkSx}
                                          >
                                            here
                                          </Box>
                                        ) : null}
                                      </Typography>
                                    </Grid>
                                    <Grid size={12}>
                                      <Typography
                                        variant="subtitle2"
                                        color="primary"
                                        sx={notificationActionTextSx}
                                      >
                                        {timeLeftText}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                }
                                secondary={
                                  <Grid container>
                                    <Typography sx={notificationListItemFlexSpacerSx} />
                                    <ManageButton
                                      manageNotificationsButton={true}
                                      section={notification}
                                    />
                                  </Grid>
                                }
                              />
                              <ListItemSecondaryAction>
                                {/* <IconButton onClick={(event) =>  { markOld({ variables: { id: notification._id }}); event.stopPropagation(); }} edge="end"> */}
                                {/* <FiberManualRecordIcon color='primary' /> */}
                                {/* </IconButton> */}
                              </ListItemSecondaryAction>
                            </ListItem>
                          )
                        },
                      )}
                      {isEmpty(oldNotifications) && !notificationsLoading && (
                        <ListItem key="no old" sx={notificationListItemCenterSx}>
                          <ListItemText>
                            No Old Notifications
                          </ListItemText>
                        </ListItem>
                      )}
                    </List>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </div>
      </Drawer>
    </>
  )
}

const mapStateToProps = (state) => ({
  headerShadow: state.DeprecatedThemeOptions.headerShadow,
  headerBgTransparent:
    state.DeprecatedThemeOptions.headerBgTransparent,
  sidebarToggleMobile:
    state.DeprecatedThemeOptions.sidebarToggleMobile,
  notificationsDrawerToggle:
    state.DeprecatedThemeOptions.notificationsDrawerToggle,
  headerFixed: state.DeprecatedThemeOptions.headerFixed,
  sidebarFixed: state.DeprecatedThemeOptions.sidebarFixed,
  sidebarToggle: state.DeprecatedThemeOptions.sidebarToggle,
})

const mapDispatchToProps = (dispatch) => ({
  setSidebarToggleMobile: (enable) =>
    dispatch(setSidebarToggleMobile(enable)),
  setNotificationsDrawerToggle: (enable) =>
    dispatch(setNotificationsDrawerToggle(enable)),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withSession(Header))
